// Janela de etiquetas de uma UC: marca/desmarca, cria, edita e apaga
// (docs/superpowers/specs/2026-10-13-etiquetas-uc-design.md).
//
// Marcar e desmarcar gravam na hora. Editar ou apagar uma etiqueta muda em todas as UCs que
// a têm — a janela diz em quantas antes de apagar.

import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Check, Loader2, Pencil, Plus, Tag, Trash2, X, AlertTriangle, ArrowLeft, RefreshCw } from 'lucide-react';
import { propsFundoModal } from '../../../src/lib/backdropModal';
import { useToast } from '../../../src/contexts/ToastContext';
import {
  CORES_ETIQUETA,
  DESCRICAO_MAX,
  ESTILO_COR,
  TEXTO_MAX,
  validarEtiqueta,
  type CorEtiqueta,
  type DadosEtiqueta,
  type Etiqueta,
} from '../../../src/lib/etiquetasUc';
import type { EtiquetasUcApi } from '../lib/useEtiquetasUc';
import { EtiquetaUcPill } from './EtiquetaUcPill';

type Props = {
  uc: { id: number; rotulo: string } | null;
  api: EtiquetasUcApi;
  onFechar: () => void;
};

type Modo = { tipo: 'lista' } | { tipo: 'nova' } | { tipo: 'editar'; etiqueta: Etiqueta };

const DADOS_VAZIOS: DadosEtiqueta = { texto: '', cor: 'fucsia', descricao: '' };

const EtiquetasUcModal: React.FC<Props> = ({ uc, api, onFechar }) => {
  const { addToast } = useToast();
  const [modo, setModo] = useState<Modo>({ tipo: 'lista' });
  const [dados, setDados] = useState<DadosEtiqueta>(DADOS_VAZIOS);
  const [tentouSalvar, setTentouSalvar] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const [ocupadaId, setOcupadaId] = useState<string | null>(null);
  const [confirmandoApagarId, setConfirmandoApagarId] = useState<string | null>(null);
  const [erroForm, setErroForm] = useState<string | null>(null);
  const textoRef = useRef<HTMLInputElement>(null);

  // Quem abre passa `key={uc.id}`: cada UC começa na lista, sem resto da anterior.
  useEffect(() => {
    if (modo.tipo !== 'lista') textoRef.current?.focus();
  }, [modo]);

  const ocupado = salvando || ocupadaId !== null;

  useEffect(() => {
    if (!uc) return;
    const aoTeclar = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' || ocupado) return;
      if (modo.tipo !== 'lista') setModo({ tipo: 'lista' });
      else onFechar();
    };
    window.addEventListener('keydown', aoTeclar);
    return () => window.removeEventListener('keydown', aoTeclar);
  }, [uc, modo, ocupado, onFechar]);

  if (!uc) return null;

  const daUc = new Set((api.porUc.get(uc.id) ?? []).map(e => e.id));
  const idEditando = modo.tipo === 'editar' ? modo.etiqueta.id : undefined;
  const motivoInvalido = validarEtiqueta(dados, api.etiquetas, idEditando);
  // Texto vazio só reclama depois de tentar salvar; repetido reclama na hora.
  const avisoForm = erroForm ?? (tentouSalvar || dados.texto.trim() ? motivoInvalido : null);

  const abrirForm = (proximo: Modo) => {
    setDados(proximo.tipo === 'editar'
      ? { texto: proximo.etiqueta.texto, cor: proximo.etiqueta.cor, descricao: proximo.etiqueta.descricao ?? '' }
      : DADOS_VAZIOS);
    setTentouSalvar(false);
    setErroForm(null);
    setConfirmandoApagarId(null);
    setModo(proximo);
  };

  const alternar = async (etiqueta: Etiqueta) => {
    if (ocupado) return;
    setOcupadaId(etiqueta.id);
    try {
      if (daUc.has(etiqueta.id)) {
        await api.tirar(uc.id, etiqueta.id);
        addToast(`"${etiqueta.texto}" saiu desta UC.`, 'success');
      } else {
        await api.aplicar(uc.id, etiqueta.id);
        addToast(`"${etiqueta.texto}" colocada nesta UC.`, 'success');
      }
    } catch (e) {
      addToast(e instanceof Error ? e.message : String(e), 'error');
    } finally {
      setOcupadaId(null);
    }
  };

  const salvar = async () => {
    setTentouSalvar(true);
    setErroForm(null);
    if (motivoInvalido || salvando) return;
    setSalvando(true);
    try {
      if (modo.tipo === 'editar') {
        await api.editar(modo.etiqueta.id, dados);
        const qtd = api.ucsPorEtiqueta.get(modo.etiqueta.id) ?? 0;
        addToast(qtd > 1 ? `Etiqueta salva. Mudou nas ${qtd} UCs que a têm.` : 'Etiqueta salva.', 'success');
      } else {
        const nova = await api.criar(dados);
        await api.aplicar(uc.id, nova.id);
        addToast(`Etiqueta "${nova.texto}" criada e colocada nesta UC.`, 'success');
      }
      setModo({ tipo: 'lista' });
    } catch (e) {
      setErroForm(e instanceof Error ? e.message : String(e));
    } finally {
      setSalvando(false);
    }
  };

  const apagar = async (etiqueta: Etiqueta) => {
    setOcupadaId(etiqueta.id);
    try {
      await api.apagar(etiqueta.id);
      setConfirmandoApagarId(null);
      addToast(`Etiqueta "${etiqueta.texto}" apagada.`, 'success');
    } catch (e) {
      addToast(e instanceof Error ? e.message : String(e), 'error');
    } finally {
      setOcupadaId(null);
    }
  };

  const fechar = () => { if (!ocupado) onFechar(); };

  return createPortal(
    <div
      className="fixed inset-0 z-[200] flex items-end sm:items-center justify-center sm:p-4 bg-black/50 backdrop-blur-sm animate-fade-in"
      {...propsFundoModal(fechar)}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="etiquetas-uc-titulo"
        className="bg-white dark:bg-gray-800 rounded-t-2xl sm:rounded-2xl shadow-xl w-full sm:max-w-md overflow-hidden animate-scale-in flex flex-col max-h-[90svh]"
        onClick={e => e.stopPropagation()}
      >
        {/* Cabeçalho */}
        <div className="px-5 py-4 border-b border-gray-100 dark:border-gray-700 flex items-start justify-between gap-3 shrink-0">
          <div className="min-w-0">
            <h2 id="etiquetas-uc-titulo" className="text-lg font-bold text-gray-800 dark:text-white flex items-center gap-2">
              {modo.tipo !== 'lista' && (
                <button
                  type="button"
                  onClick={() => setModo({ tipo: 'lista' })}
                  disabled={salvando}
                  aria-label="Voltar para a lista"
                  className="-ml-1 p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 dark:hover:text-gray-300"
                >
                  <ArrowLeft size={18} />
                </button>
              )}
              <Tag size={18} className="text-fuchsia-600 dark:text-fuchsia-400 shrink-0" />
              {modo.tipo === 'lista' ? 'Etiquetas da UC' : modo.tipo === 'nova' ? 'Nova etiqueta' : 'Editar etiqueta'}
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5 truncate">
              {modo.tipo === 'editar'
                ? 'O que mudar aqui muda em todas as UCs com esta etiqueta.'
                : uc.rotulo}
            </p>
          </div>
          <button
            type="button"
            onClick={fechar}
            disabled={ocupado}
            aria-label="Fechar"
            className="p-2 -mr-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        {modo.tipo === 'lista' ? (
          <>
            <div className="px-3 py-3 overflow-y-auto flex-1">
              {api.erroCarga && (
                <div className="mx-2 mb-3 p-3 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-sm text-red-700 dark:text-red-300 flex items-start gap-2">
                  <AlertTriangle size={16} className="shrink-0 mt-0.5" />
                  <div className="flex-1">
                    {api.erroCarga}
                    <button type="button" onClick={() => api.recarregar()} className="mt-1 flex items-center gap-1 font-semibold underline">
                      <RefreshCw size={12} /> Tentar de novo
                    </button>
                  </div>
                </div>
              )}

              {api.etiquetas.length === 0 && !api.erroCarga ? (
                <div className="px-4 py-8 text-center">
                  <div className="mx-auto w-12 h-12 rounded-full bg-fuchsia-50 dark:bg-fuchsia-900/20 flex items-center justify-center mb-3">
                    <Tag size={22} className="text-fuchsia-500" />
                  </div>
                  <p className="text-sm font-semibold text-gray-700 dark:text-gray-200">Nenhuma etiqueta criada ainda</p>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Crie a primeira para marcar esta UC. Ela fica disponível para qualquer outra UC depois.</p>
                </div>
              ) : (
                <ul className="space-y-1">
                  {api.etiquetas.map(etiqueta => {
                    const marcada = daUc.has(etiqueta.id);
                    const qtd = api.ucsPorEtiqueta.get(etiqueta.id) ?? 0;
                    const ocupada = ocupadaId === etiqueta.id;

                    if (confirmandoApagarId === etiqueta.id) {
                      return (
                        <li key={etiqueta.id} className="rounded-xl border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 p-3">
                          <p className="text-sm text-red-800 dark:text-red-200">
                            Apagar <strong>{etiqueta.texto}</strong>?{' '}
                            {qtd > 0 ? `Ela sai de ${qtd === 1 ? '1 UC' : `${qtd} UCs`}.` : 'Nenhuma UC tem esta etiqueta.'}
                          </p>
                          <div className="flex justify-end gap-2 mt-2">
                            <button
                              type="button"
                              onClick={() => setConfirmandoApagarId(null)}
                              disabled={ocupada}
                              className="px-3 py-1.5 rounded-lg text-sm font-semibold text-gray-700 dark:text-gray-200 hover:bg-white/70 dark:hover:bg-gray-700"
                            >
                              Cancelar
                            </button>
                            <button
                              type="button"
                              onClick={() => apagar(etiqueta)}
                              disabled={ocupada}
                              className="px-3 py-1.5 rounded-lg text-sm font-semibold bg-red-600 hover:bg-red-700 text-white flex items-center gap-1.5 disabled:opacity-60"
                            >
                              {ocupada ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
                              Apagar
                            </button>
                          </div>
                        </li>
                      );
                    }

                    return (
                      <li key={etiqueta.id} className={`group flex items-center gap-1 rounded-xl transition-colors ${marcada ? 'bg-fuchsia-50/60 dark:bg-fuchsia-900/10' : 'hover:bg-gray-50 dark:hover:bg-gray-700/40'}`}>
                        <button
                          type="button"
                          role="checkbox"
                          aria-checked={marcada}
                          onClick={() => alternar(etiqueta)}
                          disabled={ocupado}
                          className="flex-1 min-w-0 flex items-center gap-3 px-2 py-2.5 text-left disabled:cursor-wait"
                        >
                          <span className={`w-5 h-5 shrink-0 rounded-md border-2 flex items-center justify-center transition-colors ${marcada
                            ? 'bg-fuchsia-600 border-fuchsia-600 dark:bg-fuchsia-500 dark:border-fuchsia-500 text-white'
                            : 'border-gray-300 dark:border-gray-600'}`}
                          >
                            {ocupada ? <Loader2 size={12} className="animate-spin text-current" /> : marcada && <Check size={13} strokeWidth={3} />}
                          </span>
                          <span className="min-w-0 flex flex-col gap-0.5">
                            <EtiquetaUcPill tamanho="janela" etiqueta={etiqueta} />
                            <span className="text-[11px] text-gray-500 dark:text-gray-400 truncate">
                              {etiqueta.descricao || (qtd === 0 ? 'Em nenhuma UC' : qtd === 1 ? 'Em 1 UC' : `Em ${qtd} UCs`)}
                            </span>
                          </span>
                        </button>
                        <button
                          type="button"
                          onClick={() => abrirForm({ tipo: 'editar', etiqueta })}
                          disabled={ocupado}
                          aria-label={`Editar ${etiqueta.texto}`}
                          title="Editar texto, cor ou explicação"
                          className="p-2.5 rounded-lg text-gray-400 hover:text-gray-700 hover:bg-white dark:hover:bg-gray-700 dark:hover:text-gray-200"
                        >
                          <Pencil size={15} />
                        </button>
                        <button
                          type="button"
                          onClick={() => setConfirmandoApagarId(etiqueta.id)}
                          disabled={ocupado}
                          aria-label={`Apagar ${etiqueta.texto}`}
                          title="Apagar etiqueta de todas as UCs"
                          className="p-2.5 mr-1 rounded-lg text-gray-400 hover:text-red-600 hover:bg-white dark:hover:bg-gray-700 dark:hover:text-red-400"
                        >
                          <Trash2 size={15} />
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>

            <div className="px-5 py-4 border-t border-gray-100 dark:border-gray-700 flex items-center gap-2 shrink-0">
              <button
                type="button"
                onClick={() => abrirForm({ tipo: 'nova' })}
                disabled={ocupado}
                className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-xl border-2 border-dashed border-fuchsia-300 dark:border-fuchsia-700 text-sm font-semibold text-fuchsia-700 dark:text-fuchsia-300 hover:bg-fuchsia-50 dark:hover:bg-fuchsia-900/20 transition-colors"
              >
                <Plus size={16} /> Nova etiqueta
              </button>
              <button
                type="button"
                onClick={fechar}
                disabled={ocupado}
                className="px-4 py-2.5 rounded-xl text-sm font-semibold bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-600"
              >
                Pronto
              </button>
            </div>
          </>
        ) : (
          <form
            className="flex flex-col flex-1 min-h-0"
            onSubmit={e => { e.preventDefault(); salvar(); }}
          >
            <div className="px-5 py-4 overflow-y-auto flex-1 space-y-4">
              {/* Prévia */}
              <div className="rounded-xl bg-gray-50 dark:bg-gray-900/40 border border-gray-100 dark:border-gray-700 px-4 py-3 flex items-center justify-between gap-3">
                <span className="text-[11px] font-semibold uppercase tracking-wide text-gray-400">Prévia</span>
                <EtiquetaUcPill tamanho="janela" etiqueta={{ texto: dados.texto.trim() || 'Sua etiqueta', cor: dados.cor, descricao: dados.descricao }} />
              </div>

              <div>
                <div className="flex items-baseline justify-between mb-1.5">
                  <label htmlFor="etiqueta-texto" className="text-sm font-medium text-gray-700 dark:text-gray-300">Texto</label>
                  <span className={`text-[11px] ${dados.texto.trim().length > TEXTO_MAX ? 'text-red-500' : 'text-gray-400'}`}>
                    {dados.texto.trim().length}/{TEXTO_MAX}
                  </span>
                </div>
                <input
                  id="etiqueta-texto"
                  ref={textoRef}
                  value={dados.texto}
                  maxLength={TEXTO_MAX + 5}
                  onChange={e => { setErroForm(null); setDados(d => ({ ...d, texto: e.target.value })); }}
                  placeholder="Ex.: Conferir desconto"
                  className="w-full px-3 py-2.5 rounded-lg border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-700/50 text-base sm:text-sm text-gray-900 dark:text-white focus:ring-2 focus:ring-fuchsia-500/20 focus:border-fuchsia-500 outline-none"
                />
              </div>

              <fieldset>
                <legend className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Cor</legend>
                <div className="grid grid-cols-8 gap-2">
                  {CORES_ETIQUETA.map((cor: CorEtiqueta) => {
                    const escolhida = dados.cor === cor;
                    return (
                      <button
                        key={cor}
                        type="button"
                        onClick={() => setDados(d => ({ ...d, cor }))}
                        aria-label={ESTILO_COR[cor].nome}
                        aria-pressed={escolhida}
                        title={ESTILO_COR[cor].nome}
                        className={`aspect-square w-full max-w-[40px] mx-auto rounded-full flex items-center justify-center text-white transition-transform ${ESTILO_COR[cor].pilula} ${escolhida
                          ? 'ring-2 ring-offset-2 ring-gray-900 dark:ring-white dark:ring-offset-gray-800 scale-105'
                          : 'hover:scale-110 opacity-90 hover:opacity-100'}`}
                      >
                        {escolhida && <Check size={16} strokeWidth={3} />}
                      </button>
                    );
                  })}
                </div>
              </fieldset>

              <div>
                <div className="flex items-baseline justify-between mb-1.5">
                  <label htmlFor="etiqueta-descricao" className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    Explicação <span className="font-normal text-gray-400">(opcional)</span>
                  </label>
                  <span className="text-[11px] text-gray-400">{(dados.descricao || '').length}/{DESCRICAO_MAX}</span>
                </div>
                <textarea
                  id="etiqueta-descricao"
                  value={dados.descricao ?? ''}
                  maxLength={DESCRICAO_MAX}
                  rows={3}
                  onChange={e => setDados(d => ({ ...d, descricao: e.target.value }))}
                  placeholder="Aparece ao passar o mouse na etiqueta. Ex.: o cliente tem desconto diferente do contrato."
                  className="w-full px-3 py-2.5 rounded-lg border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-700/50 text-base sm:text-sm text-gray-900 dark:text-white focus:ring-2 focus:ring-fuchsia-500/20 focus:border-fuchsia-500 outline-none resize-none"
                />
              </div>

              {avisoForm && (
                <p role="alert" className="text-sm text-red-600 dark:text-red-400 flex items-start gap-1.5">
                  <AlertTriangle size={15} className="shrink-0 mt-0.5" /> {avisoForm}
                </p>
              )}
            </div>

            <div className="px-5 py-4 border-t border-gray-100 dark:border-gray-700 flex justify-end gap-2 shrink-0">
              <button
                type="button"
                onClick={() => setModo({ tipo: 'lista' })}
                disabled={salvando}
                className="px-4 py-2.5 rounded-xl text-sm font-semibold text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
              >
                Voltar
              </button>
              <button
                type="submit"
                disabled={salvando || (tentouSalvar && !!motivoInvalido)}
                className="px-4 py-2.5 rounded-xl text-sm font-semibold bg-fuchsia-600 hover:bg-fuchsia-700 dark:bg-fuchsia-500 dark:hover:bg-fuchsia-600 text-white flex items-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {salvando && <Loader2 size={15} className="animate-spin" />}
                {modo.tipo === 'nova' ? 'Criar e pôr nesta UC' : 'Salvar'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>,
    document.body,
  );
};

export default EtiquetasUcModal;
